import { Clock, ExternalLink, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { AsyncSelect } from "@/components/ui/async-select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  type Campaign,
  useCampaignSearch,
  useCampaignsByIds,
} from "@/hooks/api/use-campaigns";
import {
  type RecentCampaign,
  useCampaignSelection,
} from "@/hooks/use-campaign-selection";

// Union type to handle both full campaigns and recent campaigns
type CampaignOption = Campaign | RecentCampaign;

// Helper to determine if an option is a recent campaign (has timestamp, no source_record_id)
const isRecentCampaign = (
  campaign: CampaignOption,
): campaign is RecentCampaign => {
  return "timestamp" in campaign && !("source_record_id" in campaign);
};

/**
 * Renders a campaign option with name, source type, and a link to open in a new tab
 */
const renderCampaignOption = (campaign: CampaignOption) => (
  <div className="flex items-center justify-between w-full group">
    <div className="flex items-center gap-2 flex-1">
      {isRecentCampaign(campaign) && (
        <Clock className="h-3 w-3 text-muted-foreground flex-shrink-0" />
      )}
      <div className="flex flex-col min-w-0">
        <div className="font-medium">{campaign.name}</div>
        <div className="text-xs text-muted-foreground">
          {campaign.source_record_type}
        </div>
      </div>
    </div>
    <Tooltip>
      <TooltipTrigger asChild>
        <a
          href={`/campaigns/${campaign.id}/explorer`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            window.open(`/campaigns/${campaign.id}/explorer`, "_blank");
          }}
          className="ml-2"
        >
          <ExternalLink className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity" />
        </a>
      </TooltipTrigger>
      <TooltipContent side="left" sideOffset={5}>
        <p>Open in new tab</p>
      </TooltipContent>
    </Tooltip>
  </div>
);

/**
 * Campaign selector component
 *
 * Allows searching and selecting campaigns with a dropdown.
 * Shows recent campaigns and search results.
 */
export function CampaignSelector() {
  const [search, setSearch] = useState<string>();
  const {
    selectedCampaignId,
    initialCampaign,
    handleCampaignChange,
    recentCampaigns,
  } = useCampaignSelection();

  // Fetch campaigns based on search
  const { data: searchData, isFetching } = useCampaignSearch({
    search,
  });

  // Fetch selected campaign details if not found in recent campaigns
  const { data: campaignData } = useCampaignsByIds(
    !initialCampaign && selectedCampaignId ? [selectedCampaignId] : undefined,
  );

  // Determine which campaign to show as selected
  const selectedCampaign = initialCampaign ?? campaignData?.results?.[0];

  // Add the selected campaign to recent campaigns when coming from a URL
  useEffect(() => {
    if (!initialCampaign && selectedCampaignId && selectedCampaign) {
      handleCampaignChange(selectedCampaign.id, selectedCampaign);
    }
  }, [
    initialCampaign,
    selectedCampaignId,
    selectedCampaign,
    handleCampaignChange,
  ]);

  // Fetch handler for AsyncSelect component
  const fetcher = useCallback(
    async (
      searchValue?: string,
    ): Promise<{ results: CampaignOption[]; hasMore: boolean }> => {
      // Update search value if it changed
      if (searchValue !== search) {
        setSearch(searchValue || undefined);
      }

      // Determine what to show based on search and results
      if (searchValue && searchData?.results.length) {
        // Show search results
        return {
          results: searchData.results,
          hasMore: false,
        };
      }

      // If we're searching but don't have results yet, return empty array
      // but the AsyncSelect will keep showing previous results until new ones arrive
      if (searchValue && isFetching) {
        return {
          results: [],
          hasMore: false,
        };
      }

      return {
        results: recentCampaigns,
        hasMore: false,
      };
    },
    [search, searchData, recentCampaigns, isFetching],
  );

  return (
    <AsyncSelect<CampaignOption>
      fetcher={fetcher}
      preload={true}
      renderOption={renderCampaignOption}
      getOptionValue={(campaign) => campaign.id}
      getDisplayValue={(campaign) => campaign.name}
      selectedOption={selectedCampaign}
      label="Campaigns"
      placeholder="Select campaign..."
      searchPlaceholder="Search by name or source record ID"
      value={selectedCampaignId ?? ""}
      onChange={handleCampaignChange}
      loadThreshold={0.5}
      isSearching={!!search && isFetching}
      triggerClassName="bg-white dark:bg-[hsl(195_30%_12%)]"
      notFound={
        <div className="py-6 text-center text-sm">
          {isFetching ? (
            <div className="flex items-center justify-center space-x-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Searching campaigns...</span>
            </div>
          ) : search ? (
            "No campaigns found"
          ) : (
            "No recent campaigns"
          )}
        </div>
      }
      loadingSkeleton={
        <div className="p-4">
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center space-x-4">
                <div className="h-4 w-4/5 animate-pulse rounded bg-muted" />
              </div>
            ))}
          </div>
        </div>
      }
    />
  );
}
